import { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  listFiles,
  uploadFile as uploadFileApi,
  downloadFile as downloadFileApi,
  deleteFile as deleteFileApi,
  createFolder as createFolderApi,
} from '../helpers/storage';

// Storage prefix that server requires for all keys
const STORAGE_PREFIX = 'user_uploads/';

/**
 * Custom hook for managing S3 storage state and operations
 * @param {string} [initialPrefix=''] - Initial folder prefix (relative to storage root)
 * @returns {object} Storage state and operations
 */
/**
 * Strip the storage prefix from a path (for internal state)
 */
const stripPrefix = (path) => {
  if (!path) return '';
  return path.startsWith(STORAGE_PREFIX) ? path.slice(STORAGE_PREFIX.length) : path;
};

export const useStorage = (initialPrefix = '') => {

  // Navigation state (relative path within user's folder)
  const [currentPrefix, setCurrentPrefix] = useState(initialPrefix);
  const [breadcrumbs, setBreadcrumbs] = useState([]);

  // List state
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pagination state
  const [isTruncated, setIsTruncated] = useState(false);
  const [continuationToken, setContinuationToken] = useState(null);

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFileName, setUploadingFileName] = useState('');

  // Build breadcrumbs from current prefix
  useEffect(() => {
    if (!currentPrefix) {
      setBreadcrumbs([]);
      return;
    }

    const parts = currentPrefix.split('/').filter(Boolean);
    const crumbs = parts.map((part, index) => ({
      name: part,
      path: parts.slice(0, index + 1).join('/') + '/',
    }));
    setBreadcrumbs(crumbs);
  }, [currentPrefix]);

  // Load files and folders
  const loadFiles = useCallback(async (prefix = currentPrefix, append = false) => {
    setLoading(true);
    setError(null);

    try {
      const token = append ? continuationToken : null;
      // listFiles in storage.js handles adding the storage prefix
      const response = await listFiles(prefix, token);
      const data = response.data;

      if (append) {
        setFiles((prev) => [...prev, ...data.files]);
        setFolders((prev) => [...prev, ...data.folders]);
      } else {
        setFiles(data.files || []);
        setFolders(data.folders || []);
      }

      setIsTruncated(data.is_truncated || false);
      setContinuationToken(data.next_continuation_token || null);
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Failed to load files';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [currentPrefix, continuationToken]);

  // Initial load and reload on prefix change
  useEffect(() => {
    loadFiles(currentPrefix, false);
  }, [currentPrefix]); // eslint-disable-line react-hooks/exhaustive-deps

  // Navigate to folder (folderPath from API may include storage prefix, strip it)
  const navigateToFolder = useCallback((folderPath) => {
    setCurrentPrefix(stripPrefix(folderPath));
    setContinuationToken(null);
  }, []);

  // Navigate to root
  const navigateToRoot = useCallback(() => {
    setCurrentPrefix('');
    setContinuationToken(null);
  }, []);

  // Navigate to breadcrumb
  const navigateToBreadcrumb = useCallback((path) => {
    setCurrentPrefix(path);
    setContinuationToken(null);
  }, []);

  // Load more (pagination)
  const loadMore = useCallback(() => {
    if (isTruncated && continuationToken) {
      loadFiles(currentPrefix, true);
    }
  }, [isTruncated, continuationToken, currentPrefix, loadFiles]);

  // Upload file
  const uploadFile = useCallback(async (file, customPath) => {
    setUploading(true);
    setUploadProgress(0);
    setUploadingFileName(file.name);

    try {
      // Build the key path (storage.js handles adding user_uploads/ prefix)
      // Ensure folder path ends with slash before appending filename
      const prefix = currentPrefix && !currentPrefix.endsWith('/') 
        ? `${currentPrefix}/` 
        : currentPrefix;
      const key = customPath || `${prefix}${file.name}`;
      
      await uploadFileApi(file, key, (progress) => {
        setUploadProgress(progress);
      });

      toast.success(`Uploaded ${file.name}`);
      // Reload files after upload
      await loadFiles(currentPrefix, false);
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Upload failed';
      toast.error(message);
      throw err;
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadingFileName('');
    }
  }, [currentPrefix, loadFiles]);

  // Upload multiple files
  const uploadFiles = useCallback(async (fileList) => {
    for (const file of fileList) {
      await uploadFile(file);
    }
  }, [uploadFile]);

  // Download file
  const downloadFile = useCallback(async (filePath, filename) => {
    try {
      await downloadFileApi(filePath, filename);
      toast.success(`Downloaded ${filename || filePath.split('/').pop()}`);
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Download failed';
      toast.error(message);
      throw err;
    }
  }, []);

  // Delete file
  const deleteFile = useCallback(async (filePath) => {
    try {
      await deleteFileApi(filePath);
      toast.success('File deleted');
      // Reload files after delete
      await loadFiles(currentPrefix, false);
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Delete failed';
      toast.error(message);
      throw err;
    }
  }, [currentPrefix, loadFiles]);

  // Create folder
  const createFolder = useCallback(async (folderName) => {
    try {
      // Build the folder path (storage.js handles adding user_uploads/ prefix)
      // Ensure current prefix ends with slash before appending folder name
      const prefix = currentPrefix && !currentPrefix.endsWith('/') 
        ? `${currentPrefix}/` 
        : currentPrefix;
      const folderPath = `${prefix}${folderName}/`;
      await createFolderApi(folderPath);
      toast.success(`Created folder: ${folderName}`);
      // Reload files after creating folder
      await loadFiles(currentPrefix, false);
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Failed to create folder';
      toast.error(message);
      throw err;
    }
  }, [currentPrefix, loadFiles]);

  // Refresh current view
  const refresh = useCallback(() => {
    setContinuationToken(null);
    loadFiles(currentPrefix, false);
  }, [currentPrefix, loadFiles]);

  // Move items to destination folder (MOCKED)
  const moveItems = useCallback(async (itemPaths, destinationFolder) => {
    // TODO: Implement actual move API when backend supports it
    // For now, this is a mock that shows the intended operation
    console.log('Move operation (mocked):', {
      items: itemPaths,
      destination: destinationFolder,
    });
    
    const itemCount = itemPaths.length;
    const destName = destinationFolder || 'Root';
    toast.info(
      `Move ${itemCount} ${itemCount === 1 ? 'item' : 'items'} to "${destName}" (mocked - backend API needed)`
    );
    
    // In a real implementation, you would:
    // 1. For each item, copy to new location
    // 2. Delete from old location
    // 3. Refresh the file list
    // await loadFiles(currentPrefix, false);
  }, []);

  // Rename item (MOCKED)
  const renameItem = useCallback(async (oldPath, newName) => {
    // TODO: Implement actual rename API when backend supports it
    // For now, this is a mock that shows the intended operation
    const oldName = oldPath.split('/').filter(Boolean).pop();
    console.log('Rename operation (mocked):', {
      oldPath,
      oldName,
      newName,
    });
    
    toast.info(
      `Rename "${oldName}" to "${newName}" (mocked - backend API needed)`
    );
    
    // In a real implementation, you would:
    // 1. Copy item to new path with new name
    // 2. Delete old item
    // 3. Refresh the file list
    // await loadFiles(currentPrefix, false);
  }, []);

  return {
    // State
    currentPrefix,
    breadcrumbs,
    files,
    folders,
    loading,
    error,
    isTruncated,
    uploading,
    uploadProgress,
    uploadingFileName,

    // Navigation
    navigateToFolder,
    navigateToRoot,
    navigateToBreadcrumb,

    // Operations
    loadMore,
    uploadFile,
    uploadFiles,
    downloadFile,
    deleteFile,
    createFolder,
    refresh,
    moveItems,
    renameItem,
  };
};

export default useStorage;
